import config from 'config';
import Logger from 'utils/logger';
import { CognitoTokenManager } from './cognitoTokenManager';

const SEVEN_DAYS_SECONDS = 60 * 60 * 24 * 7;

function base64UrlEncode(value: object): string {
  return btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Builds a syntactically-valid but UNSIGNED JWT with a 7-day `exp`. The local api's
 * double-gated bypass ignores the token entirely and resolves the user by configured email;
 * this token exists only so the SPA's client-side token/expiry checks pass without a real
 * Cognito session. A non-local api has no bypass and rejects it, so it grants no real access.
 */
function buildSyntheticToken(email: string): string {
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: `local-dev|${email}`,
    email,
    token_use: 'access',
    iss: 'local-dev',
    iat: now,
    exp: now + SEVEN_DAYS_SECONDS,
  };

  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.local-dev`;
}

/**
 * The local dev login is reachable ONLY when BOTH hold:
 *  1. the flag is explicitly enabled (`REACT_APP_LOCAL_DEV_LOGIN === 'true'`), and
 *  2. the build is not `production`.
 */
export function isLocalDevLoginEnabled(): boolean {
  return config.LOCAL_DEV_LOGIN.enabled && config.MODE !== 'production';
}

/**
 * Seeds a synthetic local session (tokens + selected account) so the SPA bootstrap proceeds
 * into the app shell instead of redirecting to Cognito. Idempotent: no-op when disabled or when
 * a session already exists.
 */
export function seedLocalDevSessionIfEnabled(): void {
  if (!isLocalDevLoginEnabled()) {
    return;
  }

  if (CognitoTokenManager.getTokens()) {
    return;
  }

  const { email, accountId, accountName } = config.LOCAL_DEV_LOGIN;

  Logger.warn(
    '[Auth] LOCAL DEV LOGIN active — seeding a synthetic local session. Never enable outside local dev.',
  );

  const token = buildSyntheticToken(email);
  CognitoTokenManager.setTokens({
    idToken: token,
    accessToken: token,
    refreshToken: token,
  });

  if (accountId) {
    CognitoTokenManager.setSelectedAccount({
      accountId,
      accountName,
      userRole: 'admin',
      userName: email,
    });
  }
}
